import React from 'react'

import clsx from 'clsx'

import ActionButton, {
  ActionButtonSize,
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import StatusDot from '$extensionSrc/components/ImpactMetrics/StatusDot'
import LogContextProvider from '$extensionSrc/components/LogContextProvider'
import { PLATFORM } from '$extensionSrc/utils/enums'
import reportLogoBluesky2x from '$serverSrc/icons/report-logo-bluesky@2x.png'
import reportLogoBluesky3x from '$serverSrc/icons/report-logo-bluesky@3x.png'
import reportLogoFacebook2x from '$serverSrc/icons/report-logo-facebook@2x.png'
import reportLogoFacebook3x from '$serverSrc/icons/report-logo-facebook@3x.png'
import reportLogoGoogle2x from '$serverSrc/icons/report-logo-google@2x.png'
import reportLogoGoogle3x from '$serverSrc/icons/report-logo-google@3x.png'
import reportLogoInstagram2x from '$serverSrc/icons/report-logo-instagram@2x.png'
import reportLogoInstagram3x from '$serverSrc/icons/report-logo-instagram@3x.png'
import reportLogoLinkedin2x from '$serverSrc/icons/report-logo-linkedin@2x.png'
import reportLogoLinkedin3x from '$serverSrc/icons/report-logo-linkedin@3x.png'
import reportLogoReddit2x from '$serverSrc/icons/report-logo-reddit@2x.png'
import reportLogoReddit3x from '$serverSrc/icons/report-logo-reddit@3x.png'
import reportLogoSnapchat2x from '$serverSrc/icons/report-logo-snapchat@2x.png'
import reportLogoSnapchat3x from '$serverSrc/icons/report-logo-snapchat@3x.png'
import reportLogoStrava2x from '$serverSrc/icons/report-logo-strava@2x.png'
import reportLogoStrava3x from '$serverSrc/icons/report-logo-strava@3x.png'
import reportLogoTiktok2x from '$serverSrc/icons/report-logo-tiktok@2x.png'
import reportLogoTiktok3x from '$serverSrc/icons/report-logo-tiktok@3x.png'
import reportLogoTwitter2x from '$serverSrc/icons/report-logo-twitter@2x.png'
import reportLogoTwitter3x from '$serverSrc/icons/report-logo-twitter@3x.png'
import reportLogoVenmo2x from '$serverSrc/icons/report-logo-venmo@2x.png'
import reportLogoVenmo3x from '$serverSrc/icons/report-logo-venmo@3x.png'
import reportLogoYoutube2x from '$serverSrc/icons/report-logo-youtube@2x.png'
import reportLogoYoutube3x from '$serverSrc/icons/report-logo-youtube@3x.png'

import styles from './AccountSummary.module.scss'

function getReportLogoFromPlatform(
  platform: PLATFORM,
  className: string,
): JSX.Element {
  const configMapping: Record<PLATFORM, { alt: string; srcSet: string }> = {
    [PLATFORM.BLUESKY]: {
      alt: 'Bluesky',
      srcSet: `${reportLogoBluesky2x} 2x, ${reportLogoBluesky3x} 3x`,
    },
    [PLATFORM.FACEBOOK]: {
      alt: 'Facebook',
      srcSet: `${reportLogoFacebook2x} 2x, ${reportLogoFacebook3x} 3x`,
    },
    [PLATFORM.GOOGLE]: {
      alt: 'Google',
      srcSet: `${reportLogoGoogle2x} 2x, ${reportLogoGoogle3x} 3x`,
    },
    [PLATFORM.INSTAGRAM]: {
      alt: 'Instagram',
      srcSet: `${reportLogoInstagram2x} 2x, ${reportLogoInstagram3x} 3x`,
    },
    [PLATFORM.LINKEDIN]: {
      alt: 'LinkedIn',
      srcSet: `${reportLogoLinkedin2x} 2x, ${reportLogoLinkedin3x} 3x`,
    },
    [PLATFORM.REDDIT]: {
      alt: 'Reddit',
      srcSet: `${reportLogoReddit2x} 2x, ${reportLogoReddit3x} 3x`,
    },
    [PLATFORM.STRAVA]: {
      alt: 'Strava',
      srcSet: `${reportLogoStrava2x} 2x, ${reportLogoStrava3x} 3x`,
    },
    [PLATFORM.SNAPCHAT]: {
      alt: 'Snapchat',
      srcSet: `${reportLogoSnapchat2x} 2x, ${reportLogoSnapchat3x} 3x`,
    },
    [PLATFORM.TWITTER]: {
      alt: 'Twitter',
      srcSet: `${reportLogoTwitter2x} 2x, ${reportLogoTwitter3x} 3x`,
    },
    [PLATFORM.TIKTOK]: {
      alt: 'TikTok',
      srcSet: `${reportLogoTiktok2x} 2x, ${reportLogoTiktok3x} 3x`,
    },
    [PLATFORM.YOUTUBE]: {
      alt: 'YouTube',
      srcSet: `${reportLogoYoutube2x} 2x, ${reportLogoYoutube3x} 3x`,
    },
    [PLATFORM.VENMO]: {
      alt: 'Venmo',
      srcSet: `${reportLogoVenmo2x} 2x, ${reportLogoVenmo3x} 3x`,
    },
  }

  const config = configMapping[platform]

  return <img className={className} alt={config?.alt} srcSet={config?.srcSet} />
}

export default function AccountSummary({
  allAutoscansOn,
  numAccounts,
  platformsWithAccounts,
  platformsWithoutAccounts,
  timeSavedStr,
  urlAddAccounts,
  urlManageAccounts,
  urlRemovePlatforms,
}: {
  allAutoscansOn: boolean
  numAccounts: number
  platformsWithAccounts: PLATFORM[]
  platformsWithoutAccounts: PLATFORM[]
  timeSavedStr: string
  urlAddAccounts: string
  urlManageAccounts: string
  urlRemovePlatforms: string
}) {
  const logEventProps = {
    component: 'AccountSummary',
    numAccounts,
    noPlatformsWithoutAccounts: platformsWithoutAccounts?.length === 0,
    allAutoscansOn,
    timeSavedStr,
  }
  return (
    <LogContextProvider {...logEventProps}>
      <div className="AccountSummary">
        <div className={styles.main}>
          <div className={styles.summary}>
            <div className={styles.details}>
              <div className={clsx(styles.detailRow, 'heading3')}>
                <strong>Accounts protected:</strong>
                {numAccounts}
              </div>
              {timeSavedStr && (
                <div className={clsx(styles.detailRow, 'heading3')}>
                  <strong>Time saved with automations:</strong>
                  {timeSavedStr}
                </div>
              )}
              <div className={clsx(styles.detailRow, 'heading3')}>
                <strong>All autoscans on?:</strong>
                <StatusDot color={allAutoscansOn ? 'green' : 'red'} />
                {allAutoscansOn ? 'Yes' : 'No'}
              </div>
            </div>
          </div>
          <ActionButton
            className={clsx(
              styles.cta,
              platformsWithoutAccounts?.length > 0 && 'button-glow-shadow',
            )}
            size={ActionButtonSize.SMALL}
            type={ActionButtonType.SECONDARY}
            onClick={() => {
              window.location.assign(urlManageAccounts)
            }}
          >
            Manage accounts
          </ActionButton>
        </div>
        {!allAutoscansOn && (
          <div className={styles.autoscanFooter}>
            <span className="body1">
              Consider turning on autoscan for ongoing protection.
            </span>
          </div>
        )}
        <div className={styles.platformDetailSection}>
          {platformsWithAccounts.length > 0 && (
            <div className={styles.platformList}>
              <div className={styles.platformListDesc}>
                <StatusDot color="green" />
                <span className="body1">
                  {platformsWithAccounts.length === 1
                    ? 'You added accounts on 1 platform:'
                    : `You added accounts on ${platformsWithAccounts.length} platforms:`}
                </span>
              </div>
              <div className={styles.logos}>
                {platformsWithAccounts.map((x) =>
                  getReportLogoFromPlatform(x, styles.logo),
                )}
              </div>
            </div>
          )}
          {platformsWithoutAccounts?.length > 0 && (
            <>
              <div className={styles.platformList}>
                <div className={styles.platformListDesc}>
                  <StatusDot color="red" />
                  <span className="body1">
                    {platformsWithoutAccounts?.length === 1
                      ? 'You have not added accounts on 1 platform:'
                      : `You have not added accounts on ${platformsWithoutAccounts?.length}
                          platforms:`}
                  </span>
                </div>
                <div className={styles.logos}>
                  {platformsWithoutAccounts?.map((x) =>
                    getReportLogoFromPlatform(x, styles.logo),
                  )}
                </div>
              </div>
              <span className="body1">
                For a complete score, <a href={urlAddAccounts}>add accounts</a>{' '}
                for all platforms or{' '}
                <a href={urlRemovePlatforms}>remove platforms</a> you do not
                use.
              </span>
            </>
          )}
        </div>
      </div>
    </LogContextProvider>
  )
}
